/**
 * Pure array move for drag-to-reorder -- kept separate from the drag/drop DOM
 * events that trigger it so the ordering logic stays unit-testable on its own,
 * and so a list can be reordered by any control (drag, keyboard, a menu).
 *
 * A move that would change nothing, such as equal indices or an index outside
 * the list, returns the original array rather than a copy. Callers drive React
 * state with this (`setRows((current) => reorderList(current, from, to))`), and
 * an unchanged reference lets React bail out of the re-render instead of
 * re-rendering every row for a drag that landed where it started.
 *
 * The `Number.isInteger` guards are not redundant with the parameter types.
 * Indices reach this from drag libraries at runtime, and a relational
 * comparison against `null` or `undefined` does not reject them the way it
 * looks like it does -- `null >= 3` is `false`, so a null index would pass
 * every bounds check below and then be coerced to `0` by `splice`, silently
 * moving the row to the top of the list.
 */
export function reorderList<T>(
  list: ReadonlyArray<T>,
  fromIndex: number,
  toIndex: number,
): ReadonlyArray<T> {
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved as T);
  return next;
}
